import {ERRORS} from '@github-ui/item-picker-base/constants/errors'
import type {SetFieldsOperation} from '@github-ui/optimistic-connection'
import {settleConfirmedOperation} from '@github-ui/optimistic-connection/settleConfirmedOperation'
import {useMutation, useQueryClient} from '@github-ui/react-query'
import {createLinkedBranchMutation as createLinkedBranchQueryId} from '@github-ui/server-persisted-queries'
import type {
  CreateLinkedBranchInput as ServerCreateLinkedBranchInput,
  CreateLinkedBranchMutationMutation,
} from '@github-ui/server-persisted-queries/types/createLinkedBranchMutation'
import {reactFetchJSON} from '@github-ui/verified-fetch'

import {issueViewerSecondaryViewQueryKey} from '../components/fetch-issue-viewer-secondary-view'

export type CreateLinkedBranchInput = Omit<ServerCreateLinkedBranchInput, 'clientMutationId'>

type CreateLinkedBranchResponse = {
  readonly data?: CreateLinkedBranchMutationMutation | null
  readonly errors?: readonly unknown[] | null
}

type CreateLinkedBranchRoute = {
  owner: string
  repo: string
  number: number
}

const INVALID_RESPONSE_ERROR = 'Create linked branch response was invalid'

export async function fetchCreateLinkedBranch(
  {owner, repo, number}: CreateLinkedBranchRoute,
  input: CreateLinkedBranchInput,
): Promise<CreateLinkedBranchMutationMutation> {
  const {issueId: _routeBoundIssueId, ...routeSafeInput} = input
  const response = await reactFetchJSON(
    `/${encodeURIComponent(owner)}/${encodeURIComponent(repo)}/issues/${number}/linked_branches.json?queryId=${encodeURIComponent(
      createLinkedBranchQueryId,
    )}`,
    {
      method: 'POST',
      body: {input: routeSafeInput},
    },
  )

  if (!response.ok) throw new Error(`Create linked branch failed (${response.status})`)

  let json: CreateLinkedBranchResponse
  try {
    json = (await response.json()) as CreateLinkedBranchResponse
  } catch {
    throw new Error(INVALID_RESPONSE_ERROR)
  }

  const topLevelMessages = errorMessages(json.errors)
  if (topLevelMessages.length > 0) throw new Error(topLevelMessages.join(', '))
  if (!json.data) throw new Error(INVALID_RESPONSE_ERROR)

  const payload = json.data.createLinkedBranch
  if (!payload) throw new Error('Create linked branch payload was missing')

  const payloadMessages = errorMessages(payload.errors)
  if (payloadMessages.length > 0) {
    const duplicateMessage = ERRORS.branchAlreadyExists(input.name ?? '')
    if (payloadMessages.includes(duplicateMessage)) {
      const error = new Error(duplicateMessage)
      error.name = 'LinkedBranchExists'
      throw error
    }
    throw new Error(payloadMessages.join(', '))
  }

  if (!payload.linkedBranch) {
    const error = new Error('Linked branch not found')
    error.name = 'LinkedBranchNotFound'
    throw error
  }
  if (!payload.issue) throw new Error('Create linked branch issue was missing')

  return json.data
}

function errorMessages(errors: readonly unknown[] | null | undefined): string[] {
  if (!errors) return []

  return errors.flatMap(error => {
    if (!error || typeof error !== 'object' || !('message' in error)) return []
    return typeof error.message === 'string' && error.message.length > 0 ? [error.message] : []
  })
}

export function useCreateLinkedBranchMutation(route: CreateLinkedBranchRoute) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationKey: ['issue-viewer', 'create-linked-branch', route],
    mutationFn: (input: CreateLinkedBranchInput) => fetchCreateLinkedBranch(route, input),
    onSuccess: async data => {
      await settleCreateLinkedBranchMutation({queryClient, route, data})
    },
  })
}

type QueryClient = ReturnType<typeof useQueryClient>

export async function settleCreateLinkedBranchMutation({
  queryClient,
  route,
  data,
}: {
  queryClient: QueryClient
  route: CreateLinkedBranchRoute
  data: CreateLinkedBranchMutationMutation
}) {
  const issue = data.createLinkedBranch?.issue
  if (!issue) throw new Error('Create linked branch issue was missing')

  const operation: SetFieldsOperation = {
    type: 'setFields',
    id: issue.id,
    fields: {linkedBranches: issue.linkedBranches},
  }

  await settleConfirmedOperation({
    queryClient,
    targets: [{queryKey: issueViewerSecondaryViewQueryKey(route.owner, route.repo, route.number)}],
    ownerId: issue.id,
    operation,
  })
}
